import styled from 'styled-components'
import { Button } from '@stokr/components-library'
import { stoColor, stoMedia, stoRadius, stoSpace, stoType } from 'constants/stoTheme'

/** Figma: the "Eligibility steps" label row sits space/1 above the list. */
export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${stoSpace[3]};
  width: 100%;
`

export const StepsLabel = styled.p`
  ${stoType.columnLabel};
  margin: 0;
  color: ${stoColor.textSecondary};
`

export const SupportLink = styled.a`
  font-size: 11px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.4px;
  color: ${stoColor.textSecondary};
  text-decoration: none;
  white-space: nowrap;

  strong {
    font-weight: 600;
  }

  &:hover strong {
    text-decoration: underline;
  }
`

export const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[2]};
  width: 100%;
  margin-top: ${stoSpace[1]};
`

export const StepRow = styled.div<{ $done?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${stoSpace[3]};
  width: 100%;
  padding: ${stoSpace[4]};
  /* Figma: a completed step is muted with the disabled-bg fill. */
  background: ${({ $done }) => ($done ? stoColor.neutral100 : stoColor.surface)};
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};

  /* Figma 4542:26966 / 4542:27008 (mobile): text first, the action full width on the line below. */
  ${stoMedia.belowDesktop} {
    flex-direction: column;
    align-items: stretch;
  }
`

/** Below desktop the step's action (button, pill or "Completed") spans the card, label centred. */
const fullWidthBelowDesktop = `
  ${stoMedia.belowDesktop} {
    justify-content: center;
    width: 100%;
    text-align: center;
  }
`

export const StepText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`

/**
 * Figma uses a literal pure black here while the description below uses text/primary
 * (#202020) — two different blacks on adjacent lines, deliberate in the source.
 */
export const StepTitle = styled.p<{ $done?: boolean }>`
  ${stoType.bodyMdCallout};
  margin: 0;
  color: ${({ $done }) => ($done ? stoColor.disabledText : stoColor.black)};
`

export const StepDescription = styled.p<{ $done?: boolean }>`
  ${stoType.bodyMd};
  margin: 0;
  color: ${({ $done }) => ($done ? stoColor.disabledText : stoColor.textPrimary)};
`

/**
 * Only the label colour separates an actionable step from a locked one — the border is
 * the same `#e1e1e1` in both (Figma names it `action/disabled-border` even when active).
 */
export const StepBadge = styled.span`
  ${stoType.labelCta};
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  border: 2px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.full};
  white-space: nowrap;
  color: ${stoColor.disabledText};
  ${fullWidthBelowDesktop};
`

/** Figma 3278:15758: a completed step says so in plain grey label text, no border or fill,
 *  unlike the bordered pill used for locked/in-review. */
export const StepDoneLabel = styled.span`
  ${stoType.labelCta};
  flex: 0 0 auto;
  padding: 6px 16px;
  white-space: nowrap;
  color: ${stoColor.disabledText};
  ${fullWidthBelowDesktop};
`

/**
 * The shared `Button` in its `black` variant (transparent, black border/label, fills
 * black on hover/focus/active) — same CTA look as Trade's action, sized down to a
 * compact pill for this row. Only sizing/typography is overridden; colors, borders and
 * hover/active states all come from the library.
 *
 * `color` is `!important` for the same reason as `KeyActions` `FooterAction`: one call
 * site renders this `as={SmartLink}`, a real `<a>`, and the global anti-leak rule in
 * `style/base.js` (`a { color: inherit !important }`) would otherwise inherit the
 * ambient text color instead of the button's own black.
 */
export const StepBadgeButton = styled(Button)`
  ${stoType.labelCta};
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  min-height: auto;
  padding: 6px 16px;
  gap: 8px;
  white-space: nowrap;
  color: ${stoColor.black} !important;
  ${fullWidthBelowDesktop};

  /* The library's own black variant already turns the label white on hover/focus/active
     (its background fills black) — restated explicitly per-state, not as one blanket
     override above, or it would clobber that back to black-on-black. */
  &:hover,
  &:focus,
  &:active {
    color: ${stoColor.buttonLabelOnFill} !important;
  }
`
